import { Link } from "react-router-dom";
import { Heart, Handshake, Users, Sparkles, ShieldCheck, ArrowRight, Compass, Target } from "lucide-react";
import useSEO from "../hooks/useSEO";
import usePageContent from "../hooks/usePageContent";
import PageContent from "../components/PageContent";

const VALUES = [
  { key: "E", title: "Engagement", desc: "Nous prenons à cœur la mission de rapprocher les personnes, les compétences et les opportunités.", Icon: Heart },
  { key: "F", title: "Fiabilité", desc: "Un service transparent, sécurisé et digne de confiance pour tous nos utilisateurs.", Icon: ShieldCheck },
  { key: "I", title: "Inclusion", desc: "Chaque parcours mérite une place. Nous célébrons la diversité et l'accessibilité.", Icon: Users },
  { key: "S", title: "Solidarité", desc: "Nous croyons à l'entraide, à la coopération et au bien commun.", Icon: Handshake },
  { key: "I2", title: "Innovation", desc: "La technologie au service d'une intégration plus humaine et plus efficace.", Icon: Sparkles },
];

const TEAM = [
  { name: "Patrick Demanou Kana", role: "Partenariats et communication", desc: "Développe les partenariats stratégiques et pilote les communications de DemkaBiz.", photo: "/media/team/patrick-demanou-kana.jpg" },
  { name: "Cathy Olivia Atieufack", role: "Soutien administratif", desc: "Assure le suivi administratif et la coordination des activités quotidiennes.", photo: "/media/team/cathy-olivia-atieufack.jpg" },
  { name: "Winnie D. Gouajeu", role: "Service à la clientèle", desc: "Accueille, informe et accompagne les utilisateurs dans leurs démarches.", photo: "/media/team/winnie-gouajeu.jpg" },
  { name: "Glory Nsenga", role: "Gestion de communauté", desc: "Anime les communautés en ligne et favorise l'engagement des membres.", photo: "/media/team/glory-nsenga.jpg" },
  { name: "Agnès Dorice Olemba", role: "Gestion de communauté · point focal Afrique", desc: "Coordonne les relations avec les communautés et partenaires en Afrique.", photo: "/media/team/agnes-dorice-olemba.jpg" },
  { name: "Sosthène Yonta", role: "Projets et partenariats", desc: "Contribue au développement des projets et à la mise en œuvre des collaborations stratégiques.", photo: "/media/team/sosthene-yonta.jpg" },
  { name: "Romario T. Tegnikeng", role: "Soutien technique", desc: "Assure l'assistance technique et le bon fonctionnement des outils numériques.", photo: "/media/team/romario-tegnikeng.jpg" },
  { name: "Franck Brandolph Kana", role: "Agent de relais — Point focal Afrique", desc: "Relaie les activités de Demka Services Inc. en Afrique et coordonne les partenaires locaux depuis Douala (Cameroun).", photo: "/media/team/franck-brandolph-kana.jpg", placeholder: "FB" },
];

export default function About() {
  useSEO({
    title: "À propos",
    description: "DemkaBiz, une initiative de Demka Services Inc. — la plateforme francophone qui simplifie l'établissement et l'entrepreneuriat des nouveaux arrivants au Canada.",
  });
  const mission = usePageContent("about.mission", "Simplifier l'accès aux services d'établissement et d'entrepreneuriat pour faciliter l'intégration des nouveaux-arrivants et promouvoir l'entrepreneuriat immigrant francophone au Canada.");
  const vision = usePageContent("about.vision", "Devenir la référence numérique francophone au Canada pour l'établissement des personnes immigrantes, l'entrepreneuriat et la connexion avec l'Afrique.");

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-14 sm:py-20" data-testid="about-page">
      {/* Hero */}
      <div className="grid lg:grid-cols-[1fr_auto] items-end gap-6">
        <div>
          <p className="text-xs uppercase tracking-widest text-primary font-semibold mb-2">À propos</p>
          <h1 className="font-display font-bold text-4xl sm:text-5xl lg:text-6xl text-foreground tracking-tight leading-[1.05]">
            Une plateforme <span className="text-primary">humaine</span>,<br />
            née d'une expérience <span className="text-accent italic">vécue</span>.
          </h1>
        </div>
        <p className="text-muted-foreground max-w-md leading-relaxed lg:text-right lg:pb-3">
          Découvrez la genèse, la mission et les visages qui portent chaque jour DemkaBiz.
        </p>
      </div>

      {/* Genèse */}
      <section className="mt-16 grid lg:grid-cols-2 gap-10 items-start">
        <div className="space-y-4 text-foreground/85 leading-relaxed">
          <h2 className="font-display font-bold text-2xl sm:text-3xl">Genèse</h2>
          <p><strong className="text-foreground">Tout commence par une expérience vécue.</strong></p>
          <p>
            En arrivant au Canada, notre fondateur a fait face au parcours parfois complexe des nouveaux arrivants
            francophones : trouver un logement, un professionnel de confiance, un service dans sa langue, une
            information fiable… souvent dispersés, difficiles d'accès, et sans point de contact centralisé.
          </p>
          <p>
            De cette expérience est né <strong>DemkaBiz</strong> : une plateforme francophone qui rassemble en
            un seul endroit les prestataires, entrepreneurs et organismes qui aident à s'établir, entreprendre
            et réussir au Canada — et bientôt, à connecter le Canada à l'Afrique.
          </p>
        </div>
        <div className="rounded-3xl overflow-hidden bg-secondary">
          <img
            src="/media/team/patrick-portrait.jpg"
            alt="Portrait de Patrick Demanou Kana, fondateur de DemkaBiz"
            className="w-full h-full object-cover object-center max-h-[520px]"
          />
        </div>
      </section>

      {/* Qui sommes-nous */}
      <section className="mt-16 card-surface p-6 sm:p-10">
        <h2 className="font-display font-bold text-2xl sm:text-3xl">Qui sommes-nous ?</h2>
        <p className="text-foreground/85 mt-3 leading-relaxed max-w-4xl">
          <strong className="text-foreground">DemkaBiz</strong> est une place de marché digitale francophone qui
          centralise des microservices d'établissement et d'entrepreneuriat, et facilite la mise en relation entre
          prestataires et clients au sein d'un écosystème 100 % francophone au Canada. Développée par{" "}
          <strong>Demka Services Inc.</strong>, entreprise établie à Québec, DemkaBiz s'adresse aux nouveaux
          arrivants, aux professionnels, aux entrepreneurs immigrants et aux organismes communautaires.
        </p>
      </section>

      {/* Mission & Vision */}
      <section className="mt-10 grid md:grid-cols-2 gap-5">
        <div className="card-surface p-6 sm:p-8">
          <div className="inline-flex items-center gap-2 text-primary mb-3">
            <Target className="w-5 h-5" />
            <span className="text-xs uppercase tracking-widest font-semibold">Mission</span>
          </div>
          <p className="text-lg font-display leading-snug" data-testid="about-mission-text"><PageContent value={mission} /></p>
        </div>
        <div className="card-surface p-6 sm:p-8">
          <div className="inline-flex items-center gap-2 text-accent mb-3">
            <Compass className="w-5 h-5" />
            <span className="text-xs uppercase tracking-widest font-semibold">Vision</span>
          </div>
          <p className="text-lg font-display leading-snug" data-testid="about-vision-text"><PageContent value={vision} /></p>
        </div>
      </section>

      {/* Valeurs EFISI */}
      <section className="mt-16">
        <div className="text-center max-w-2xl mx-auto">
          <p className="text-xs uppercase tracking-widest text-accent font-semibold mb-2">Nos valeurs</p>
          <h2 className="font-display font-bold text-3xl sm:text-4xl">
            <span className="text-primary">E</span>ngagement · <span className="text-primary">F</span>iabilité ·{" "}
            <span className="text-primary">I</span>nclusion · <span className="text-primary">S</span>olidarité ·{" "}
            <span className="text-primary">I</span>nnovation
          </h2>
          <p className="text-muted-foreground mt-3">Cinq valeurs qui guident chaque décision, chaque mise en relation, chaque service rendu.</p>
        </div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-5 gap-4 mt-10">
          {VALUES.map((v) => (
            <div key={v.key} className="card-surface p-5 hover:shadow-md transition-shadow" data-testid={`value-${v.title.toLowerCase()}`}>
              <div className="w-10 h-10 rounded-full bg-secondary text-primary flex items-center justify-center mb-3">
                <v.Icon className="w-5 h-5" />
              </div>
              <h3 className="font-display font-semibold text-base">{v.title}</h3>
              <p className="text-xs text-muted-foreground mt-1.5 leading-relaxed">{v.desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Ce qui nous distingue */}
      <section className="mt-16 rounded-3xl bg-primary text-primary-foreground p-8 sm:p-12">
        <h2 className="font-display font-bold text-2xl sm:text-3xl">Ce qui nous distingue</h2>
        <div className="grid md:grid-cols-3 gap-6 mt-6">
          <div>
            <div className="font-display font-bold text-3xl text-accent">100%</div>
            <p className="text-white/90 text-sm mt-1">Francophone, pensé pour les personnes qui parlent, vivent et rêvent en français.</p>
          </div>
          <div>
            <div className="font-display font-bold text-3xl text-accent">Humain</div>
            <p className="text-white/90 text-sm mt-1">Chaque demande est suivie par une équipe qui écoute, oriente et accompagne.</p>
          </div>
          <div>
            <div className="font-display font-bold text-3xl text-accent">Afrocanadien</div>
            <p className="text-white/90 text-sm mt-1">Un pont concret entre le Canada et le continent africain pour l'entrepreneuriat.</p>
          </div>
        </div>
      </section>

      {/* Équipe */}
      <section className="mt-20" data-testid="about-team">
        <div className="text-center max-w-2xl mx-auto">
          <p className="text-xs uppercase tracking-widest text-primary font-semibold mb-2">Notre équipe</p>
          <h2 className="font-display font-bold text-3xl sm:text-4xl">Des visages, des expertises, une mission commune</h2>
          <p className="text-muted-foreground mt-3">
            L'équipe DemkaBiz est composée de personnes engagées, réparties au Canada et en Afrique, qui travaillent chaque jour à connecter les compétences aux opportunités.
          </p>
        </div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5 mt-10">
          {TEAM.map((m) => (
            <div key={m.name} className="card-surface overflow-hidden group" data-testid={`team-${m.name.split(' ')[0].toLowerCase()}`}>
              <div className="aspect-[3/4] bg-secondary overflow-hidden">
                <img
                  src={m.photo}
                  alt={`${m.name} — ${m.role}`}
                  className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                  loading="lazy"
                />
              </div>
              <div className="p-4">
                <h3 className="font-display font-semibold text-base leading-tight">{m.name}</h3>
                <p className="text-xs uppercase tracking-wider text-primary font-medium mt-1">{m.role}</p>
                <p className="text-xs text-muted-foreground mt-2 leading-relaxed">{m.desc}</p>
              </div>
            </div>
          ))}
        </div>
        <div className="rounded-2xl bg-secondary/60 border border-primary/10 p-5 mt-6 flex items-start gap-3">
          <div className="flex-shrink-0 w-10 h-10 rounded-full bg-primary/10 text-primary flex items-center justify-center font-display font-bold">
            PN
          </div>
          <div>
            <p className="font-display font-semibold">Passerelle Numérique Inc.</p>
            <p className="text-xs uppercase tracking-wider text-primary font-medium mt-0.5">Support informatique</p>
            <p className="text-sm text-muted-foreground mt-1 leading-relaxed">
              Fournit l'hébergement, la maintenance et le soutien informatique de la plateforme.
            </p>
          </div>
        </div>
      </section>

      {/* Ressources gouvernementales */}
      <section className="mt-16" data-testid="about-resources">
        <div className="text-center max-w-2xl mx-auto">
          <p className="text-xs uppercase tracking-widest text-primary font-semibold mb-2">Ressources en ligne</p>
          <h2 className="font-display font-bold text-2xl sm:text-3xl">Sources officielles utiles</h2>
          <p className="text-muted-foreground mt-2 text-sm">Une sélection de ressources du Gouvernement du Canada pour vous accompagner dans votre parcours.</p>
        </div>
        <div className="grid md:grid-cols-2 gap-4 mt-8">
          {[
            { n: "#1", title: "Provinces et territoires — Apprendre à connaître le Canada", body: "Apprenez-en davantage au sujet des provinces et des territoires du Canada, y compris les services qui vous sont offerts pour vous établir et les ressources à l'intention des immigrants d'expression française.", url: "https://www.canada.ca/fr/immigration-refugies-citoyennete/services/setablir-canada/provinces-territoires.html" },
            { n: "#2", title: "La vie en français au Canada", body: "Des immigrants d'expression française et leurs familles expliquent pourquoi ils ont choisi de vivre dans des communautés francophones hors Québec.", url: "https://www.canada.ca/fr/immigration-refugies-citoyennete/nouvelles/video/vie-francais-canada.html" },
            { n: "#3", title: "Envisagez de vous établir dans une collectivité francophone hors Québec", body: "Le français est l'une des 2 langues officielles du Canada. Bon nombre de francophones habitent hors Québec.", url: "https://www.canada.ca/fr/immigration-refugies-citoyennete/services/setablir-canada/communautes-francophones.html" },
            { n: "#4", title: "Choisir une communauté accueillante offrant des services en français", body: "24 communautés francophones hors Québec s'engagent activement à accueillir les nouveaux arrivants d'expression française.", url: "https://www.canada.ca/fr/immigration-refugies-citoyennete/campagnes/immigration-francophone-hors-quebec/bienvenue.html" },
          ].map((r, i) => (
            <a key={i} href={r.url} target="_blank" rel="noopener noreferrer" data-testid={`resource-${i+1}`} className="card-surface p-5 hover:shadow-md transition-shadow group">
              <div className="flex items-start gap-3">
                <span className="flex-shrink-0 inline-flex items-center justify-center w-11 h-11 rounded-full bg-primary/10 text-primary font-display font-bold">
                  {r.n}
                </span>
                <div className="flex-1 min-w-0">
                  <p className="text-[11px] uppercase tracking-widest text-accent font-semibold">Gouvernement du Canada</p>
                  <h3 className="font-display font-semibold text-base leading-snug mt-1 group-hover:text-primary transition-colors">{r.title}</h3>
                  <p className="text-sm text-muted-foreground mt-2 leading-relaxed">{r.body}</p>
                  <span className="text-xs text-primary hover:underline mt-2 inline-block">Consulter la ressource →</span>
                </div>
              </div>
            </a>
          ))}
        </div>
      </section>

      {/* Appel à bénévoles */}
      <section className="mt-16 rounded-3xl bg-accent/10 border-2 border-dashed border-accent/30 p-8 sm:p-12" data-testid="about-volunteers">
        <div className="grid lg:grid-cols-[auto_1fr] gap-6 items-start">
          <div className="flex-shrink-0 w-16 h-16 rounded-2xl bg-accent flex items-center justify-center">
            <Heart className="w-8 h-8 text-white" />
          </div>
          <div>
            <p className="text-xs uppercase tracking-widest text-accent font-semibold mb-2">Appel à bénévoles</p>
            <h2 className="font-display font-bold text-2xl sm:text-3xl">DemkaBiz se construit avec sa communauté</h2>
            <p className="text-foreground/85 mt-3 leading-relaxed">
              Vous croyez en une francophonie économique forte et inclusive au Canada ? <strong>Rejoignez l'aventure.</strong>
            </p>
            <p className="text-foreground/85 mt-3 leading-relaxed">
              Nous recherchons des bénévoles prêts à mettre leur temps et leurs compétences au service des nouveaux arrivants, ainsi que des organismes, prestataires et partenaires qui souhaitent gagner en visibilité et enrichir notre écosystème.
            </p>
            <a
              href="mailto:contact@demka.ca?subject=Bénévolat%20DemkaBiz"
              data-testid="volunteers-cta"
              className="inline-flex items-center gap-2 mt-6 px-5 py-3 rounded-full bg-accent hover:bg-accent/90 text-white font-medium transition"
            >
              Écrivez-nous : contact@demka.ca
            </a>
          </div>
        </div>
      </section>

      {/* Collaborer */}
      <section className="mt-16 card-surface p-8 sm:p-12 text-center">
        <p className="text-xs uppercase tracking-widest text-accent font-semibold mb-2">Collaborer</p>
        <h2 className="font-display font-bold text-3xl sm:text-4xl tracking-tight">Rejoignez l'aventure DemkaBiz</h2>
        <p className="text-muted-foreground mt-3 max-w-2xl mx-auto leading-relaxed">
          Que vous soyez professionnel, entrepreneur, organisme communautaire ou partenaire institutionnel :
          venez faire partie d'une plateforme qui met la francophonie et la diaspora au cœur de l'action.
        </p>
        <div className="flex flex-wrap items-center justify-center gap-3 mt-8">
          <Link to="/inscription" className="btn-primary" data-testid="about-cta-register">
            Créer un compte <ArrowRight className="w-4 h-4" />
          </Link>
          <Link to="/contact" className="btn-outline" data-testid="about-cta-contact">Nous contacter</Link>
        </div>
      </section>
    </div>
  );
}
